/// <amd-module name="Widgets/WitChartWidget/WitChartWidget" />
import * as React from "react";

import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import * as VssLocation from "VSS/Platform/Location";
import * as VssUserClaims from "VSS/Platform/UserClaims";

import { ChartProviders } from "Tfs/Charting/ChartProviders";
import { ChartConfiguration } from "Tfs/Charting/Contracts/Reporting";
import * as TfsWidgetContext from "Tfs/Widget/Context";

import { CommonChartWidget } from "WidgetCommon/CommonChart/CommonChartWidget";

import { WidgetProps, WidgetSize } from "Dashboards/WidgetContracts/WidgetProps";
import { isFeatureEnabled } from "VSS/Platform/Feature";


import { WidgetHost } from "Dashboards/WidgetHost/WidgetHost";
import { WidgetPresentationMode } from "Dashboards/WidgetContracts/WidgetProps";
import { WidgetSizeConverter } from "Dashboards/Grid/WidgetSizeConverter";
import { DashboardWidgetProps, WidgetGridSize } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";

import { Lightbox } from "Reporting/Lightbox/Lightbox";
import {
    IGlobalDialog,
    IGlobalMessagesService
} from "TFS/GlobalBanner/GlobalMessagesService.Types";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Link } from "VSSUI/Link";
import * as WidgetResources from "Widgets/WitChartWidget/Resources";
import { ISettingsRestClient } from "VSS/Platform/RestClient/Settings";


export class WitChartWidget extends VssComponent<WidgetProps> {
    private readonly announcementId = "WITAnnouncement";

    constructor(props: WidgetProps, context: IVssComponentContext) {
        super(props);
    }

    public render(): JSX.Element {
        return (
            <CommonChartWidget
                hasClickPermission={this.canUserClickThruChart()}
                featureName={ChartProviders.witQueries}
                className={"witchart-container"}
                getLinkUrl={this.getLinkUrl}
                linkDescription={"QueryLink"}
                openLightBox={this.openLightboxView}
                {...this.props}
            />
        );
    }

    componentDidMount(): void {
        this.showAnnouncementDialog();        
    }

    private canUserClickThruChart(): boolean {
        return VssUserClaims.userHasClaim(this.context.pageContext, VssUserClaims.UserClaims.Member);
    }

    /**
     * Get the URL to the Query Builder. This is used to generate the url for the graph click event.
     */
    private getLinkUrl = (chartConfiguration: ChartConfiguration | undefined, queryText?: string): string => {
        const queryResultsRouteId = this.getRouteId();
        const queryResultsViewId = "query";
        const projectId = TfsWidgetContext.getProject(this.context.pageContext).id;
    
        let routeUrl = (chartConfiguration && !queryText) ? VssLocation.routeUrl(this.context.pageContext, queryResultsRouteId, {
            project: projectId,
            view: queryResultsViewId,
            id: chartConfiguration.groupKey
        }) : VssLocation.routeUrl(this.context.pageContext, queryResultsRouteId, {
            project: projectId,
            view: queryResultsViewId
        });
    
        if (queryText) {
            routeUrl = routeUrl.substring(0, routeUrl.length - 1) + "?wiql=" + encodeURIComponent(queryText);
        }
    
        return routeUrl;
    };    

    private getRouteId(): string{
        if (isFeatureEnabled(this.context.pageContext, "ms.vss-work-web.new-boards-hub-feature", false)){
            return "ms.vss-work-web.new-queries-hub-route";
        }

        return "ms.vss-work-web.query-route";
    }

    private openLightboxView(props: WidgetProps, layoutManager: IVssLayoutManager){
        const newprops : DashboardWidgetProps = {
            contributionId: props.contributionId,
            name: props.name,

            size: WidgetSizeConverter.ConvertToGridSize(props.size),
            settings: props.settings,
            // Use custom setting, or apply smart default
            settingsVersion: props.settingsVersion || { major: 1, minor: 0, patch: 0 },

            // These should be optional for embed scenarios
            isEnabled: true,
            position: {
                column: 0,
                row: 0
            },
            areSettingsBlockedForUser: false,
            configurationContributionId: "FAKE - UNUSED",
            isNameConfigurable: false
        };
        
        layoutManager.renderCallout(onDismiss => {
            return (
                <Lightbox onDismiss={onDismiss} title={"SSSSS"}>
                    <div style={{width: window.innerWidth * 0.85,height: window.innerHeight * 0.85}}>
                        <div className="widget-host render-in-lightbox" style={{width: window.innerWidth * 0.85,height: window.innerHeight * 0.85}}>
                            <WidgetHost
                                key={0}
                                widgetPresentationMode={WidgetPresentationMode.lightboxView}
                                parentPageName={"Dashboard"}
                                widgetProps={newprops}
                                shouldNotShowTitle={true}
                                userCanConfigure={false}
                                openLinksInNewTab={true}
                                showLoadAnimations={true}
                                teamContext={props.teamContext} 
                                convertToPixelSize={function (size: WidgetGridSize): WidgetSize {
                                    return {
                                        width: props.size.height,
                                        height: props.size.width
                                    };} 
                                }
                            />
                        </div>
                    </div>
                </Lightbox>
            );
        });
    }

    private async showAnnouncementDialog(): Promise<void>{
        const pageContext = this.context.pageContext;
        const messageService = pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        const settingsClient = pageContext.getRestClient<ISettingsRestClient>("ISettingsRestClient");
        const viewAnnouncementSettings = await settingsClient.getEntries("me","GlobalDialog/Dismissed");
        const dialog = messageService.getDialog();
        if((dialog && dialog.settingId === this.announcementId) ||
           (viewAnnouncementSettings && viewAnnouncementSettings[this.announcementId]))
            return;
        messageService.addDialog({
            componentType: "cornerDialog",
            settingId: this.announcementId,
            contentProperties: {
                titleProps: { text: WidgetResources.WITChart_Announcement_Title },
                showCloseButton: true,
                children: (
                    <FormatComponent className="message" format={WidgetResources.WITChart_Announcement_Message}>
                        <Link
                            href="https://devblogs.microsoft.com/devops/introducing-work-item-chart-filtering-in-azure-devops-dashboards"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {WidgetResources.WITChart_Announcement_LearnMore}
                        </Link>
                    </FormatComponent>
                ),
                onDismiss: () => {
                    messageService.closeDialog();
                }
            },
        } as IGlobalDialog);
    }
}

VssComponent.register("witChartWidget", WitChartWidget);
